import React, { useRef } from "react";
import {
  Box,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
  Flashbar,
} from "@amzn/awsui-components-react";
import { COLUMN_DEFINITIONS } from "../config/searchTableConfig";
import { useSearchHistory } from "../hooks/useSearchHistory";
import useFolders from "../hooks/useFolders";
import { SavedSearchItem, SearchHistoryItem } from "../types/common";
import { FolderModal } from "./folders/FolderModal";
import { useFolderUI } from "../hooks/useFolderUI";

export default function SearchHistory() {
  const textFilterRef = useRef(null);

  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI();

  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
    deleteSelectedItems,
  } = useSearchHistory();
  const { folders, addTransactions, saveFolder } = useFolders();

  const handleAddToFolder = () => {
    if (!collectionProps.selectedItems?.length) {
      return;
    }
    setIsModalVisible(true);
  };

  const handleFolderSelect = () => {
    if (selectedFolderId && collectionProps.selectedItems?.length) {
      const selectedTransactions = (
        collectionProps.selectedItems as SearchHistoryItem[]
      ).map(
        (item) =>
          ({
            id: item.orderId,
            stage: item.stage,
            status: item.status,
            country: item.countryCode || "-",
            gl: item.glProductGroup,
            usecase: "",
            paymentMethod: "",
          } as SavedSearchItem)
      );

      const result = addTransactions(selectedFolderId, selectedTransactions);

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });

      if (result.addedCount > 0 && collectionProps.onSelectionChange) {
        collectionProps.onSelectionChange({ detail: { selectedItems: [] } });
      }
      handleModalDismiss();
    }
  };

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
      </div>
      <Table
        {...collectionProps}
        resizableColumns
        stickyHeader
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        loadingText="Loading resources"
        selectionType="multi"
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={handleAddToFolder}
                  disabled={collectionProps.selectedItems?.length === 0}
                >
                  Add to Folder
                </Button>
                <Button
                  onClick={deleteSelectedItems}
                  disabled={collectionProps.selectedItems?.length === 0}
                >
                  Delete selected
                </Button>
              </SpaceBetween>
            }
          >
            Recent Searches
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            ref={textFilterRef}
            filteringPlaceholder="Filter by Order ID, GL, Country, Stage, or Status"
          />
        }
        pagination={<Pagination {...paginationProps} />}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No recent searches</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              Transactions you search for will appear here.
            </Box>
          </Box>
        }
      />

      <FolderModal
        isModalVisible={isModalVisible}
        isFolderModalVisible={isFolderModalVisible}
        isShareModalVisible={false}
        selectedFolderId={selectedFolderId}
        folderName={folderName}
        error={error}
        folders={folders}
        sharingFolder={null}
        onModalDismiss={handleModalDismiss}
        onCreateFolder={handleCreateFolder}
        onFolderSelect={handleFolderSelect}
        onFolderModalDismiss={handleFolderModalDismiss}
        onShareModalDismiss={() => {}}
        onSaveFolder={() => handleSaveFolder(saveFolder)}
        setSelectedFolderId={setSelectedFolderId}
        setFolderName={setFolderName}
      />
    </>
  );
}
